import React, { useState, useEffect, useRef, useCallback } from "react";
import { StageElement, ImageElementConfig } from "../../../types";
import type { ViewerDeviceRuntime } from "../../../types";
import { resolveImageUrlForDevice } from "../../../util/resolveOfflinePackageImageUrl";
import {
  StyleFigure,
  StyleCaption,
  StyleLayoutItem,
  StyleLongDescription,
  StyleViewerIMG,
  ImageContainer,
} from "./styles";
import { percentify, selectCompressedImage } from "./helpers";
import { CAPTION_ABOVE, CAPTION_BELOW, ALIGNMENT_CENTER } from "./constants";
import { Media } from "@bronte/viewer";

const ImageElementViewer = ({
  element,
  onMediaClick,
  viewerDevice,
}: {
  element: StageElement;
  onMediaClick?: (data: Media) => void;
  viewerDevice?: ViewerDeviceRuntime;
}) => {
  const config = element.config as ImageElementConfig;
  const [img, setImg] = useState<{
    src: string;
    alt: string;
    captionText: string;
    credit: string;
    longDescription: string;
    captionPosition: string;
    alignment: string;
    size: string | number;
  }>({
    src: "",
    alt: "",
    captionText: "",
    credit: "",
    longDescription: "",
    captionPosition: CAPTION_BELOW,
    alignment: ALIGNMENT_CENTER,
    size: 100,
  });
  const [textHeight, setTextHeight] = useState(0);
  const [aspectRatio, setAspectRatio] = useState(0);

  const imgRef = useRef<HTMLImageElement>(null);
  const captionRef = useRef<HTMLElement>(null);
  const creditRef = useRef<HTMLDivElement>(null);

  const getAspectRatio = (width?: number, height?: number) =>
    !width || !height ? 0 : parseFloat((width / height).toFixed(3));

  const handleImageClick = useCallback(() => {
    if (!onMediaClick || !img.src) return;
    const payload: Media = {
      mediaType: "Image",
      metadata: {
        src: img.src,
        alt: img.alt || "",
        caption: img.captionText || "",
      },
    };
    onMediaClick(payload);
  }, [onMediaClick, img.src, img.alt, img.captionText]);

  useEffect(() => {
    if (!config || !config.url) {
      return;
    }

    let alt: string, caption: string, credit: string, longDescription: string;
    const captionPosition = config.layout?.captionPosition || CAPTION_BELOW;
    const alignment = config.layout?.alignment || ALIGNMENT_CENTER;
    const size = config.size?.width === 0 ? 0 : percentify(config.size?.width || 100);
    const url = config.url;

    if (typeof url === "string" && !url.startsWith("urn")) {
      alt = config.alt || "";
      caption = config.caption || "";
      credit = config.credit || "";
      longDescription = "";
      const finalSize = size >= 0 ? size : "auto";
      setImg({
        src: resolveImageUrlForDevice(url, viewerDevice),
        alt,
        credit,
        longDescription,
        captionText: caption,
        captionPosition,
        size: finalSize,
        alignment,
      });
    } else if (typeof url === "object" && url?.toString() === "[object Object]") {
      alt = url?.metadata?.altText || config.alt || "";
      caption = config.caption || "";
      credit = config.credit || "";
      longDescription = url?.metadata?.longDesc || "";

      const sourceObj = url.source || {};
      let chosenSrc = selectCompressedImage({
        original: sourceObj.original,
        compressed: sourceObj.compressed,
      });

      // Fallback to large, medium, small if original/compressed not available
      if (!chosenSrc?.url) {
        chosenSrc = sourceObj.large || sourceObj.medium || sourceObj.small || sourceObj.original;
      }

      if (chosenSrc) {
        const aspectRatioValue = getAspectRatio(chosenSrc.width, chosenSrc.height);
        setAspectRatio(aspectRatioValue);
        const imageUrl = resolveImageUrlForDevice(chosenSrc.url || "", viewerDevice);
        const finalSize = size >= 0 ? size : "auto";
        setImg({
          src: imageUrl,
          alt,
          credit,
          longDescription,
          captionText: caption,
          captionPosition,
          size: finalSize,
          alignment,
        });
      }
    }
  }, [config, viewerDevice]);

  useEffect(() => {
    setTimeout(() => {
      let height = 0;
      if (captionRef?.current) {
        const { current } = captionRef;
        height =
          height +
          current.offsetHeight +
          (parseFloat(current.style.marginTop) || 0) +
          (parseFloat(current.style.marginBottom) || 0);
      }

      if (creditRef?.current) {
        const { current } = creditRef;
        height =
          height +
          current.offsetHeight +
          (parseFloat(current.style.marginTop) || 0) +
          (parseFloat(current.style.marginBottom) || 0);
      }

      setTextHeight(height);
    }, 0);
  }, [img]);

  if (!config || !config.url || !img.src) {
    return null;
  }

  const alignmentLeft = img.alignment === "Right" || img.alignment === "Center" ? "auto" : 0;
  const alignmentRight = img.alignment === "Left" || img.alignment === "Center" ? "auto" : 0;

  return (
    <>
      {config && config.url ? (
        <StyleFigure className={config.cssClass || ""} data-ref={element.ref}>
          {img.captionPosition === CAPTION_ABOVE && img.captionText && (
            <StyleCaption ref={captionRef as React.RefObject<HTMLElement>}>
              <StyleLayoutItem
                data-id={`${element.ref}-caption`}
                className="component-caption"
                dangerouslySetInnerHTML={{ __html: img.captionText }}
              />
            </StyleCaption>
          )}

          <ImageContainer
            className="image-container"
            width={config.size?.width}
            aspectRatio={aspectRatio}
            alignmentLeft={alignmentLeft}
            alignmentRight={alignmentRight}
            textHeight={textHeight}
          >
            <StyleViewerIMG
              ref={imgRef}
              src={img.src}
              alt={img.alt}
              size={img.size}
              alignmentLeft={alignmentLeft}
              alignmentRight={alignmentRight}
              textHeight={textHeight}
              onClick={handleImageClick}
            />
          </ImageContainer>

          {img.captionPosition === CAPTION_BELOW && img.captionText && (
            <StyleCaption ref={captionRef as React.RefObject<HTMLElement>}>
              <StyleLayoutItem
                data-id={`${element.ref}-caption`}
                className="component-caption"
                dangerouslySetInnerHTML={{ __html: img.captionText }}
              />
            </StyleCaption>
          )}

          {img.credit && (
            <StyleLayoutItem
              ref={creditRef}
              data-id={`${element.ref}-credit`}
              className="layout component-credit"
              dangerouslySetInnerHTML={{ __html: img.credit }}
            />
          )}

          {img.longDescription && (
            <StyleLongDescription
              className="Long-desc-content"
              dangerouslySetInnerHTML={{
                __html: img.longDescription,
              }}
            />
          )}
        </StyleFigure>
      ) : null}
    </>
  );
};

export default ImageElementViewer;
